import { useCallback, useEffect, useRef, useState } from "react";
import { BuyerAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/BuyerAPIHandler";
import { validateEmail, validatePassword } from "../../../../utils/validators";
import { OTP_LENGTH, PasswordResetStep } from "../../../../enums/AuthFlowEnums";
import { useOtpResendCountdown } from "./useOtpResendCountdown";

const SUCCESS_REDIRECT_DELAY_MS = 2000;

/** Single source of truth for the password reset journey, shared by web and mobile. */
export const usePasswordResetFlow = (onComplete: () => void, initialEmail: string = "") => {
    const [step, setStep] = useState<PasswordResetStep>(PasswordResetStep.Email);
    const [email, setEmail] = useState(initialEmail);
    const [otp, setOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const { canResend, resendButtonText, restartCountdown } = useOtpResendCountdown();

    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    useEffect(() => {
        if (step !== PasswordResetStep.Success) return;

        const timerId = window.setTimeout(() => onCompleteRef.current(), SUCCESS_REDIRECT_DELAY_MS);
        return () => window.clearTimeout(timerId);
    }, [step]);

    const run = async (operation: () => Promise<void>) => {
        setError("");
        setIsLoading(true);
        try {
            await operation();
        } catch (requestError) {
            setError(requestError as string);
        } finally {
            setIsLoading(false);
        }
    };

    const sendOtp = async () => {
        const emailValidation = validateEmail(email);
        if (!emailValidation.status) {
            setError(emailValidation.error);
            return;
        }

        await run(async () => {
            await BuyerAPIHandler.shared.sendOtpForResetPassword(email);
            restartCountdown();
            setStep(PasswordResetStep.Otp);
        });
    };

    const verifyOtp = async () => {
        if (otp.length !== OTP_LENGTH) return;

        await run(async () => {
            await BuyerAPIHandler.shared.verifyOtpForResetPassword(email, otp);
            setStep(PasswordResetStep.NewPassword);
        });
    };

    const resendOtp = async () => {
        setError("");
        setIsResending(true);
        try {
            await BuyerAPIHandler.shared.sendOtpForResetPassword(email);
            restartCountdown();
        } catch (requestError) {
            setError(requestError as string);
        } finally {
            setIsResending(false);
        }
    };

    const changePassword = async () => {
        const passwordValidation = validatePassword(newPassword);
        if (!passwordValidation.status) {
            setError(passwordValidation.error);
            return;
        }
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        await run(async () => {
            await BuyerAPIHandler.shared.updateBuyer({ password: newPassword });
            setStep(PasswordResetStep.Success);
        });
    };

    const updateEmail = useCallback((value: string) => {
        setEmail(value);
        setError("");
    }, []);

    return {
        step,
        email,
        setEmail: updateEmail,
        otp,
        setOtp,
        newPassword,
        setNewPassword,
        confirmPassword,
        setConfirmPassword,
        error,
        isLoading,
        isResending,
        canResend,
        resendButtonText,
        sendOtp,
        verifyOtp,
        resendOtp,
        changePassword,
        backToEmailStep: () => setStep(PasswordResetStep.Email),
        goToOtpStep: () => setStep(PasswordResetStep.Otp),
        goToNewPasswordStep: () => setStep(PasswordResetStep.NewPassword),
    };
};

export type PasswordResetFlowController = ReturnType<typeof usePasswordResetFlow>;

export default usePasswordResetFlow;
